// Users API service for fetching users for sharing features

import { apiBaseUrl } from '../../config/env';
import { SocialLinks } from '../../types/auth';

const API_URL = apiBaseUrl;

export interface AppUser {
  id: string;
  name: string;
  phone: string;
  profession?: string;
  industry?: string;
  city?: string;
  photo?: string;
}

export interface UsersSearchResponse {
  success: boolean;
  users: AppUser[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface ProfileUpdateData {
  // Profile photo
  profilePhoto?: string;
  
  // Contact fields
  name?: string;
  email?: string;
  dateOfBirth?: string;
  company?: string;
  designation?: string;
  gender?: string;
  country?: string;
  state?: string;
  city?: string;
  industry?: string;
  profession?: string;
  
  // Personal fields
  spouse?: string;
  children?: string;
  animals?: string;
  hobbies?: string;
  activityOfInterest?: string;
  cityOfResidence?: string;
  burningDesire?: string;
  somethingNoOneKnows?: string;
  keyToSuccesses?: string;
  personnelGoals?: string;
  personnelAccomplishments?: string;
  personnelInterests?: string;
  personnelNetwork?: string;
  personnelSkills?: string;
  
  // Business fields
  businessName?: string;
  businessLocation?: string;
  previousTypeOfJobs?: string;
  businessGoals?: string;
  businessAccomplishments?: string;
  businessInterests?: string;
  businessNetwork?: string;
  businessSkills?: string;
  
  // Business Profile fields
  businessPhoto?: string;
  businessTitle?: string;
  businessIndustry?: string;
  businessType?: string;
  businessCountry?: string;
  businessPlace?: string;
  productsServices?: string;
  noOfEmployees?: string;
  businessMobileNumber?: string;
  linkedinId?: string;
  companyEmail?: string;
  businessWebsite?: string;
  shareValue?: string;
  businessLink?: string;
  businessLocationFull?: string;
  aboutCompany?: string;

  // Social Links
  socialLinks?: SocialLinks;
}

export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  user: any;
}

// Search for users with pagination
export const searchUsers = async (
  token: string,
  search: string = '',
  page: number = 1,
  limit: number = 10
): Promise<UsersSearchResponse> => {
  const response = await fetch(
    `${API_URL}/api/users/search?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to search users');
  }

  return response.json();
};

// Update user profile
export const updateProfile = async (
  token: string,
  data: ProfileUpdateData
): Promise<UpdateProfileResponse> => {
  const response = await fetch(
    `${API_URL}/api/users/me`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update profile');
  }

  return response.json();
};

// Get a specific user's profile by ID
export const getUserProfileById = async (
  token: string,
  userId: string
): Promise<{ success: boolean; user: any }> => {
  const response = await fetch(
    `${API_URL}/api/users/${userId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch user profile');
  }

  return response.json();
};

